// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Lock, Users, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { thrownMessage } from '@/lib/apiErrors';
import { deriveListQuery } from '@/lib/listQuery';
import type { GroupListItem, UserGroup, UserListItem } from '@greenlight/shared';

import { splitMemberships } from './groups-logic';

const PAGE_SIZE = 100;

export interface UserGroupsPanelProps {
  user: UserListItem;
  /** org_admin — membership is an access-control decision (docs/41 §RBAC). */
  canManage: boolean;
  onUserUpdated?: (updated: UserListItem) => void;
}

/**
 * A user's group memberships from the user side. Greenlight memberships are
 * edited here; directory ones are the IdP's answer and render locked, per
 * group rather than per org (ADR-0064 decision 4).
 */
export function UserGroupsPanel({
  user,
  canManage,
  onUserUpdated,
}: UserGroupsPanelProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const memberships = user.groups ?? [];
  const { greenlight, idp } = splitMemberships(memberships);

  const [pendingGroupId, setPendingGroupId] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const groupsQuery = api.groups.list.useQuery(
    ['groups'],
    { query: { limit: PAGE_SIZE } },
    { queryKey: ['groups'], enabled: canManage },
  );
  const { items: allGroups, nextCursor } = deriveListQuery<GroupListItem>({
    data: groupsQuery.data,
    isError: groupsQuery.isError,
  });

  const joined = new Set(greenlight.map((g) => g.id));
  const joinable = allGroups.filter((g) => !joined.has(g.id));

  const settle = (groups: UserGroup[]): void => {
    onUserUpdated?.({ ...user, groups });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const addMember = api.groups.addMember.useMutation();
  const removeMember = api.groups.removeMember.useMutation();

  const handleAdd = async (): Promise<void> => {
    const group = joinable.find((g) => g.id === pendingGroupId);
    if (!group || busy) return;
    setBusy(true);
    try {
      const res = await addMember.mutateAsync({
        params: { id: group.id },
        body: { user_id: user.id },
      });
      if (res.status === 201) {
        toast.success(`Added to ${group.name}.`);
        setPendingGroupId('');
        settle([
          ...memberships,
          {
            id: group.id,
            name: group.name,
            description: group.description,
            source: 'greenlight',
          },
        ]);
      } else {
        toast.error("Couldn't add that membership.");
      }
    } catch (err) {
      toast.error(thrownMessage(err, "Couldn't add that membership."));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (group: UserGroup): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await removeMember.mutateAsync({
        params: { id: group.id, userId: user.id },
      });
      if (res.status === 204) {
        toast.success(`Removed from ${group.name}.`);
        settle(memberships.filter((g) => !(g.source === 'greenlight' && g.id === group.id)));
      } else {
        toast.error("Couldn't remove that membership.");
      }
    } catch (err) {
      toast.error(thrownMessage(err, "Couldn't remove that membership."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="user-groups-panel">
      {memberships.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">Not a member of any group yet.</p>
      ) : (
        <div className="rounded-gl border border-border bg-card divide-y divide-border overflow-hidden">
          {greenlight.map((group) => (
            <div key={`gl-${group.id}`} className="flex items-start gap-3 px-4 py-3">
              <div className="shrink-0 flex items-center justify-center w-7 h-7 rounded-gl-icon bg-muted border border-border">
                <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[12.5px] font-medium text-foreground">{group.name}</span>
                {group.description != null && (
                  <p className="mt-0.5 text-[12px] text-muted-foreground">{group.description}</p>
                )}
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">Greenlight group</p>
              </div>
              {canManage && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 md:h-7 md:w-7"
                  aria-label={`Remove from ${group.name}`}
                  disabled={busy}
                  onClick={() => void handleRemove(group)}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}

          {idp.map((group) => {
            const named = group.name !== group.id;
            return (
              <div key={`idp-${group.id}`} className="flex items-start gap-3 px-4 py-3">
                <div className="shrink-0 flex items-center justify-center w-7 h-7 rounded-gl-icon bg-muted border border-border">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <span
                    className={
                      named
                        ? 'text-[12.5px] font-medium text-foreground'
                        : 'font-mono text-[12px] text-foreground break-all'
                    }
                  >
                    {group.name}
                  </span>
                  <p className="mt-0.5 inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                    <Lock className="h-3 w-3" aria-hidden="true" />
                    Managed in your identity provider
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canManage && joinable.length === 0 && nextCursor !== null && (
        <p className="text-[11.5px] text-muted-foreground">
          Showing the first {PAGE_SIZE} groups by name, and this user is in all of them — add from
          the group&apos;s own member list if the one you want is not here.
        </p>
      )}

      {canManage && joinable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={pendingGroupId} onValueChange={setPendingGroupId}>
            <SelectTrigger className="h-11 md:h-9 w-full sm:w-56" aria-label="Add to group">
              <SelectValue placeholder="Add to a group…" />
            </SelectTrigger>
            <SelectContent>
              {joinable.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-11 md:h-9"
            disabled={pendingGroupId === '' || busy}
            onClick={() => void handleAdd()}
          >
            Add
          </Button>
          {nextCursor !== null && (
            <p className="basis-full text-[11.5px] text-muted-foreground">
              Showing the first {PAGE_SIZE} groups by name — add from the group&apos;s own member
              list if it is not here.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
